import {useState, useEffect, useCallback, useRef} from "react";
import {ImbalanceItem, StatusFilter} from "../data/types";
import {ImbalanceFilter} from "../../graphql/audit-overview-api/api";
import {imbalanceService} from "../../components/shared/ImbalanceService";

type UseImbalancesResult = {
    data: ImbalanceItem[];
    totalCount: number | null;
    loading: boolean;
    loadingMore: boolean;
    hasMore: boolean;
    error: string | null;
    loadMoreError: string | null;
    loadMore: () => void;
};

export function useImbalances(
    month: string,
    isPersistent?: boolean,
    filter?: ImbalanceFilter,
    material?: string,
    size?: string,
    excludeColors?: boolean,
    statusFilter?: StatusFilter,
    refreshKey?: number,
): UseImbalancesResult {
    const [data, setData] = useState<ImbalanceItem[]>([]);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
    const nextTokenRef = useRef<string | null>(null);
    const fetchIdRef = useRef(0);
    const loadingMoreRef = useRef(false);

    useEffect(() => {
        const currentFetchId = ++fetchIdRef.current;
        setLoading(true);
        setError(null);
        setLoadMoreError(null);
        setLoadingMore(false);
        loadingMoreRef.current = false;
        nextTokenRef.current = null;
        setHasMore(false);
        setTotalCount(null);

        imbalanceService
            .fetchImbalances(month, material || undefined, size || undefined, isPersistent, filter, excludeColors, statusFilter)
            .then(page => {
                if (currentFetchId !== fetchIdRef.current) return;
                setData(page.items);
                setTotalCount(page.totalCount);
                nextTokenRef.current = page.nextToken;
                setHasMore(page.nextToken !== null);
                setLoading(false);
            })
            .catch(err => {
                if (currentFetchId !== fetchIdRef.current) return;
                setError(err?.message ?? 'Failed to load imbalances');
                setLoading(false);
            });
    }, [month, isPersistent, filter, material, size, excludeColors, statusFilter, refreshKey]);

    const loadMore = useCallback(() => {
        if (!nextTokenRef.current || loadingMoreRef.current) return;
        const currentFetchId = fetchIdRef.current;

        loadingMoreRef.current = true;
        setLoadingMore(true);
        setLoadMoreError(null);
        imbalanceService
            .fetchImbalances(month, material || undefined, size || undefined, isPersistent, filter, excludeColors, statusFilter, nextTokenRef.current)
            .then(page => {
                if (currentFetchId !== fetchIdRef.current) return;
                setData(prev => [...prev, ...page.items]);
                nextTokenRef.current = page.nextToken;
                setHasMore(page.nextToken !== null);
                loadingMoreRef.current = false;
                setLoadingMore(false);
            })
            .catch(err => {
                if (currentFetchId !== fetchIdRef.current) return;
                setLoadMoreError(err?.message ?? 'Failed to load more results');
                loadingMoreRef.current = false;
                setLoadingMore(false);
            });
    }, [month, isPersistent, filter, material, size, excludeColors, statusFilter]);

    return {data, totalCount, loading, loadingMore, hasMore, error, loadMoreError, loadMore};
}
